import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { AiButton } from '../../../web/components-v2/AiButton';
import { IDemoRecording } from '../../queries/DemoRecordings/useDemoRecordings';
import { styleSheet } from './styles';

interface IRecordingItemProps {
	recording: IDemoRecording;
	isBestMatch: boolean;
	onPlay?: (url: string) => void;
	onDownloadTranscript?: (url: string) => void;
	onAnalyze?: (recording: IDemoRecording) => void;
	isAnalyzeLoading?: boolean;
}

export const RecordingItem: React.FC<IRecordingItemProps> = ({
	recording,
	isBestMatch,
	onPlay,
	onDownloadTranscript,
	onAnalyze,
	isAnalyzeLoading = false,
}) => {
	const startTime = moment(recording.startTime);
	const formattedTime = startTime.format('h:mm A');
	const formattedDate = startTime.format('MMM D, YYYY');
	// Format duration as "X hours Y minutes" or just "Y minutes" if less than 1 hour
	const formatDuration = (minutes: number): string => {
		const hours = Math.floor(minutes / 60);
		const remainingMinutes = minutes % 60;

		return hours > 0 ? (remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`) : `${remainingMinutes}m`;
	};

	const formattedDuration = formatDuration(recording.duration);

	const handlePlay = () => {
		// Use actualVideoRecording first, fallback to original videoRecording
		if (videoRecording && onPlay) {
			// Prioritize shareUrl (meeting share url) over playUrl (view online)
			const shareUrl = videoRecording.shareUrl;
			const playUrl = videoRecording.playUrl;
			const downloadUrl = videoRecording.downloadUrl;

			// Choose the best URL: shareUrl > playUrl > downloadUrl
			const urlToUse = shareUrl || playUrl || downloadUrl;

			if (urlToUse) {
				onPlay(urlToUse);
			}
		}
	};

	const handleDownloadTranscript = () => {
		// Use actualTranscript first, fallback to original transcript
		if (transcript?.downloadUrl && onDownloadTranscript) {
			onDownloadTranscript(transcript.downloadUrl);
		}
	};

	const handleAnalyze = () => {
		if (onAnalyze) {
			onAnalyze(recording);
		}
	};

	// Get the closer from internal participants (longest duration participant)
	const closerParticipant = recording.participants
		.filter(p => p.name && p.name.trim())
		.sort((a, b) => b.duration - a.duration)[0]; // Sort by duration descending, take first

	const closerName = closerParticipant?.name || 'Unknown Closer';

	const externalParticipantNames = recording.externalParticipants
		.map(p => p.name)
		.filter(name => name && name.trim())
		.join(', ');

	const externalParticipantEmails = recording.externalParticipants
		.map(p => p.email)
		.filter(email => email && email.trim())
		.join(', ');

	const videoRecording = recording.actualVideoRecording || recording.videoRecording;
	// Show play button if we have any playable URL
	const hasPlayableUrl =
		videoRecording && (videoRecording.shareUrl || videoRecording.playUrl || videoRecording.downloadUrl);

	const transcript = recording.actualTranscript || recording.transcript;

	return (
		<div className={css(styleSheet.recordingItem, isBestMatch && styleSheet.bestMatch)}>
			{isBestMatch && <div className={css(styleSheet.bestMatchBadge)}>Most Likely Match</div>}

			<div className={css(styleSheet.recordingHeader)}>
				<div className={css(styleSheet.timeInfo)}>
					<div>
						<span className={css(styleSheet.date)}>{formattedDate}</span>
						<span className={css(styleSheet.time)}> at {formattedTime} ET</span>
					</div>
					<span className={css(styleSheet.duration)}>• {formattedDuration}</span>
				</div>

				<div className={css(styleSheet.actions)}>
					{hasPlayableUrl && (
						<button
							type='button'
							onClick={handlePlay}
							className={css(styleSheet.actionButton, styleSheet.playButton)}
							aria-label={`Play recording from ${formattedDate} at ${formattedTime}`}
						>
							▶ Play
						</button>
					)}
					{transcript?.downloadUrl && (
						<button
							type='button'
							onClick={handleDownloadTranscript}
							className={css(styleSheet.actionButton, styleSheet.transcriptButton)}
							aria-label={`Download transcript for recording from ${formattedDate} at ${formattedTime}`}
						>
							📄 Transcript
						</button>
					)}
					{onAnalyze && (
						<AiButton
							title='Analyze'
							onClick={handleAnalyze}
							size='sm'
							isLoading={isAnalyzeLoading}
							aria-label={`Analyze recording from ${formattedDate} at ${formattedTime}`}
						/>
					)}
				</div>
			</div>

			{recording.topic && <div className={css(styleSheet.topic)}>{recording.topic}</div>}

			{closerParticipant && (
				<div className={css(styleSheet.participants)}>
					<span className={css(styleSheet.participantLabel)}>Closer:</span>
					<span className={css(styleSheet.participantNames)} title={closerParticipant.email}>
						{closerName}
					</span>
				</div>
			)}

			{externalParticipantNames && (
				<div className={css(styleSheet.participants)}>
					<span className={css(styleSheet.participantLabel)}>Attendees:</span>
					<span className={css(styleSheet.participantNames)} title={externalParticipantEmails}>
						{externalParticipantNames}
					</span>
				</div>
			)}
		</div>
	);
};
